// src/services/datev/DatevExportsService.ts

import { netlifyFetch } from '../../shared/services/netlifyFetch';

// ============================================
// TYPESCRIPT INTERFACES (basierend auf hr:exports OpenAPI v1.0.8)
// ============================================

export interface DatevExportClient {
  client_id: string;
  consultant_number: number;
  client_number: number;
}

export interface DatevExportEmployeeIds {
  personnel_number: number;
  company_personnel_number: string;
}

// MasterData
export interface DatevExportMasterData {
  personnel_number: number;
  company_personnel_number: string;
  accounting_month: string;
  month_of_recalculation: string;
  employment?: DatevExportEmployment;
  taxes?: DatevExportTaxes;
  social_security?: DatevExportSocialSecurity;
  personal_data?: DatevExportPersonalData;
}

export interface DatevExportEmployment {
  employee_status?: string;
  start_of_employment?: string;
  end_of_employment?: string;
  job_title?: string;
  cost_center?: DatevExportCostCenter;
  employee_address?: DatevExportEmployeeAddress;
  // weitere Felder optional je nach Antwort
  [key: string]: any;
}

export interface DatevExportCostCenter {
  cost_center_number?: string;
  cost_center_name?: string;
  [key: string]: any;
}

export interface DatevExportEmployeeAddress {
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  [key: string]: any;
}

export interface DatevExportTaxes {
  tax_class?: string;
  religious_affiliation?: string;
  tax_id?: string;
  [key: string]: any;
}

export interface DatevExportSocialSecurity {
  social_security_number?: string;
  health_insurance?: string;
  [key: string]: any;
}

export interface DatevExportPersonalData {
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  gender?: string;
  nationality?: string;
  [key: string]: any;
}

// TaxPayments
export interface DatevExportTaxPayments {
  personnel_number: number;
  company_personnel_number: string;
  accounting_month: string;
  month_of_recalculation: string;
  current_gross_tax?: number;
  flat_rate_church_tax?: number;
  flat_rate_solidarity_tax?: number;
  flat_rate_wage_tax?: number;
  flat_rate_taxed_payments?: number;
  [key: string]: any;
}

// SocialSecurityPayments
export interface DatevExportSocialSecurityPayments {
  personnel_number: number;
  company_personnel_number: string;
  accounting_month: string;
  month_of_recalculation: string;
  health_insurance?: any;
  unemployment_insurance?: any;
  long_term_care_insurance?: any;
  pension_insurance?: any;
  current_payments_to_social_security_employers_contribution?: number;
  other_payments_to_social_security_employers_contribution?: number;
  [key: string]: any;
}

// SalaryTotalValues
export interface DatevExportSalaryTotalValues {
  personnel_number: number;
  company_personnel_number: string;
  accounting_month: string;
  month_of_recalculation: string;
  current_gross_payment?: number;
  amount_paid?: number;
  net_income?: number;
  net_payments_and_net_deductions?: number;
  total_statutory_deductions?: number;
  [key: string]: any;
}

// SalaryPayments
export interface DatevExportSalaryPayments {
  personnel_number: number;
  company_personnel_number: string;
  accounting_month: string;
  month_of_recalculation: string;
  gross_payments_lodas?: any[];
  gross_payments_lug?: any[];
  net_payments?: any[];
}

// Absences
export interface DatevExportAbsences {
  personnel_number: number;
  company_personnel_number: string;
  accounting_month: string;
  month_of_recalculation: string;
  total_vacation_entitlement?: number;
  vacation_days_taken?: number;
  remaining_vacation_days_previous_year?: number;
  remaining_vacation_days_current_year?: number;
  sick_leave_month?: number;
  sick_leave_hours?: number;
  overtime?: number;
}

export interface DatevExportError {
  error: string;
  datev_error?: string;
  help_url?: string;
  requires_reconnect?: boolean;
}

export interface DatevExportFilter {
  payroll_accounting_month?: string;
  payroll_accounting_month_end?: string;
  payroll_recalculation_month?: string;
  payroll_recalculation_month_end?: string;
}

export interface DatevExportResponse<T> {
  data: T;
  payrollSystem?: string; // X-Payroll-System Header
}

// ============================================
// SERVICE
// ============================================

class DatevExportsService {
  private getApiBaseUrl(): string {
    return 'https://intranet.xtax.de/.netlify/functions/datev-exports';
  }

  private buildQuery(filter?: DatevExportFilter): string {
    if (!filter) return '';
    const params = new URLSearchParams();
    if (filter.payroll_accounting_month) {
      params.append('payroll_accounting_month', filter.payroll_accounting_month);
    }
    if (filter.payroll_accounting_month_end) {
      params.append('payroll_accounting_month_end', filter.payroll_accounting_month_end);
    }
    if (filter.payroll_recalculation_month) {
      params.append('payroll_recalculation_month', filter.payroll_recalculation_month);
    }
    if (filter.payroll_recalculation_month_end) {
      params.append('payroll_recalculation_month_end', filter.payroll_recalculation_month_end);
    }
    const s = params.toString();
    return s ? `&${s}` : '';
  }

  private async fetchJson<T>(path: string, customerId: string): Promise<T> {
    const sep = path.includes('?') ? '&' : '?';
    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}${path}${sep}customerId=${customerId}`
    );
    if (!response.ok) {
      const error: DatevExportError = await response.json();
      throw error;
    }
    return response.json();
  }

  // ============================================
  // BERECHTIGUNGSPRÜFUNG
  // ============================================

  /** GET /clients — Liste aller freigegebenen Bestände */
  async getClients(customerId: string): Promise<DatevExportClient[]> {
    return this.fetchJson<DatevExportClient[]>('/clients', customerId);
  }

  /**
   * GET /clients/{client-id} — Berechtigungsprüfung für einen Bestand
   *
   * WICHTIG: Laut DATEV OpenAPI-Spec gibt dieser Endpoint Status 200 OHNE Body zurück.
   * Status 200 = Berechtigung vorhanden, 403 = keine Berechtigung, 400 = ungültige client-id.
   * Wir konstruieren das Client-Objekt aus der client-id (Format: "berater-mandant").
   */
  async getClient(customerId: string, clientId: string): Promise<DatevExportClient> {
    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}/clients/${clientId}?customerId=${customerId}`
    );

    if (!response.ok) {
      // Bei Fehler ist die Response JSON
      const error: DatevExportError = await response.json();
      throw error;
    }

    // Status 200 ohne Body → Berechtigung bestätigt
    // Client-Objekt aus der client-id ableiten (Format: "consultant-client")
    const parts = clientId.split('-');
    return {
      client_id: clientId,
      consultant_number: parseInt(parts[0], 10) || 0,
      client_number: parseInt(parts[1], 10) || 0
    };
  }

  // ============================================
  // EMPLOYEE-ID LOOKUP
  // ============================================

  /** GET /clients/{client-id}/employeeids/{company-personnel-number} */
  async getEmployeeIds(
    customerId: string,
    clientId: string,
    companyPersonnelNumber: string
  ): Promise<DatevExportEmployeeIds> {
    return this.fetchJson<DatevExportEmployeeIds>(
      `/clients/${clientId}/employeeids/${encodeURIComponent(companyPersonnelNumber)}`,
      customerId
    );
  }

  // ============================================
  // EINZELNE MITARBEITER-ENDPOINTS
  // ============================================

  async getEmployeeMasterData(
    customerId: string,
    clientId: string,
    employeeId: number,
    filter?: DatevExportFilter
  ): Promise<DatevExportMasterData> {
    return this.fetchJson<DatevExportMasterData>(
      `/clients/${clientId}/employees/${employeeId}/masterdata?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getEmployeeTaxPayments(
    customerId: string,
    clientId: string,
    employeeId: number,
    filter?: DatevExportFilter
  ): Promise<DatevExportTaxPayments> {
    return this.fetchJson<DatevExportTaxPayments>(
      `/clients/${clientId}/employees/${employeeId}/taxpayments?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getEmployeeSocialSecurityPayments(
    customerId: string,
    clientId: string,
    employeeId: number,
    filter?: DatevExportFilter
  ): Promise<DatevExportSocialSecurityPayments> {
    return this.fetchJson<DatevExportSocialSecurityPayments>(
      `/clients/${clientId}/employees/${employeeId}/socialsecuritypayments?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getEmployeeSalaryTotalValues(
    customerId: string,
    clientId: string,
    employeeId: number,
    filter?: DatevExportFilter
  ): Promise<DatevExportSalaryTotalValues> {
    return this.fetchJson<DatevExportSalaryTotalValues>(
      `/clients/${clientId}/employees/${employeeId}/salarytotalvalues?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getEmployeeSalaryPayments(
    customerId: string,
    clientId: string,
    employeeId: number,
    filter?: DatevExportFilter
  ): Promise<DatevExportSalaryPayments> {
    return this.fetchJson<DatevExportSalaryPayments>(
      `/clients/${clientId}/employees/${employeeId}/salarypayments?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getEmployeeAbsences(
    customerId: string,
    clientId: string,
    employeeId: number,
    filter?: DatevExportFilter
  ): Promise<DatevExportAbsences> {
    return this.fetchJson<DatevExportAbsences>(
      `/clients/${clientId}/employees/${employeeId}/absences?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  // ============================================
  // SAMMELABFRAGEN (alle Mitarbeiter)
  // ============================================

  async getAllMasterData(
    customerId: string,
    clientId: string,
    filter?: DatevExportFilter
  ): Promise<DatevExportMasterData[]> {
    return this.fetchJson<DatevExportMasterData[]>(
      `/clients/${clientId}/employees/masterdata?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getAllTaxPayments(
    customerId: string,
    clientId: string,
    filter?: DatevExportFilter
  ): Promise<DatevExportTaxPayments[]> {
    return this.fetchJson<DatevExportTaxPayments[]>(
      `/clients/${clientId}/employees/taxpayments?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getAllSocialSecurityPayments(
    customerId: string,
    clientId: string,
    filter?: DatevExportFilter
  ): Promise<DatevExportSocialSecurityPayments[]> {
    return this.fetchJson<DatevExportSocialSecurityPayments[]>(
      `/clients/${clientId}/employees/socialsecuritypayments?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getAllSalaryTotalValues(
    customerId: string,
    clientId: string,
    filter?: DatevExportFilter
  ): Promise<DatevExportSalaryTotalValues[]> {
    return this.fetchJson<DatevExportSalaryTotalValues[]>(
      `/clients/${clientId}/employees/salarytotalvalues?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getAllSalaryPayments(
    customerId: string,
    clientId: string,
    filter?: DatevExportFilter
  ): Promise<DatevExportSalaryPayments[]> {
    return this.fetchJson<DatevExportSalaryPayments[]>(
      `/clients/${clientId}/employees/salarypayments?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }

  async getAllAbsences(
    customerId: string,
    clientId: string,
    filter?: DatevExportFilter
  ): Promise<DatevExportAbsences[]> {
    return this.fetchJson<DatevExportAbsences[]>(
      `/clients/${clientId}/employees/absences?${this.buildQuery(filter).slice(1)}`,
      customerId
    );
  }
}

export const datevExportsService = new DatevExportsService();
